import { Box, FormControl, MenuItem, Select, Stack, Typography } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import { useState } from 'react';
import Chart from 'react-apexcharts';

import { colors } from 'src/global/styles/colors';
import { useProjectDashboardContext } from 'src/hooks/useProjectDashboardContext';
import { useGetProjectFundingDistributionsQuery } from 'src/store/services/project-dashboard.service';

const FundDistributionChart = () => {
  const [filter, setFilter] = useState('All');
  const { projectId, address, network } = useProjectDashboardContext();

  const {
    data: distributionData,
    isLoading,
    error,
  } = useGetProjectFundingDistributionsQuery(
    { projectId, address, network },
    { skip: !projectId || !address || !network },
  );

  const chartSeries = distributionData?.data
    ? Object.entries(distributionData.data).map(([recipientName, data]) => ({
        name: recipientName,
        data: data.map(item => item.y),
      }))
    : [];

  const seriesColors = ['#495DB6', '#F36848', '#4CC3C1', '#7C6DF9', '#26A17B'];
  const chartColorsArray = chartSeries.map((_, index) => seriesColors[index % seriesColors.length]);

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 296,
      toolbar: {
        show: false,
      },
      background: 'transparent',
      fontFamily: 'Gotham, sans-serif',
    },
    theme: {
      mode: 'dark',
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        borderRadius: 2,
      },
    },
    dataLabels: {
      enabled: false,
    },
    grid: {
      borderColor: 'rgba(255, 255, 255, 0.03)',
      strokeDashArray: 6,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    xaxis: {
      categories: distributionData?.data ? Object.values(distributionData.data)[0]?.map(item => item.s) || [] : [],
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: colors.neutral90,
          fontSize: '12px',
          fontFamily: 'Gotham, sans-serif',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: colors.neutral90,
          fontSize: '12px',
          fontFamily: 'Gotham, sans-serif',
        },
        formatter: value => `$${value}k`,
      },
      tickAmount: 6,
    },
    tooltip: {
      theme: 'dark',
      style: {
        fontSize: '12px',
        fontFamily: 'Gotham, sans-serif',
      },
      y: {
        formatter: value => `$${value}`,
      },
    },
    legend: {
      show: true,
      position: 'bottom',
      offsetY: 0,
      itemMargin: {
        vertical: 6,
      },
      markers: {
        strokeWidth: 0,
        size: 8,
        shape: 'square',
      },
    },
    colors: chartColorsArray,
  };

  return (
    <Box
      sx={{
        background: colors.neutral03,
        backdropFilter: 'blur(100px)',
        borderRadius: '8px',
        padding: '24px',
        height: '432px',
      }}>
      <Stack spacing={2} height='100%'>
        {/* Header */}
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography
            sx={{
              color: colors.white,
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '24px',
            }}>
            Funding Distribution
          </Typography>

          <FormControl size='small'>
            <Select
              value={filter}
              onChange={e => setFilter(e.target.value)}
              sx={{
                backgroundColor: colors.neutral03,
                color: colors.white,
                fontSize: '12px',
                borderRadius: '4px',
                minWidth: '100px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
                '& .MuiSvgIcon-root': {
                  color: colors.white,
                },
              }}>
              <MenuItem value='All'>All</MenuItem>
              {distributionData?.data &&
                Object.keys(distributionData.data).map(recipient => (
                  <MenuItem key={recipient} value={recipient}>
                    {recipient}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Stack>

        {/* Chart */}
        <Box sx={{ flex: 1, width: '100%' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography sx={{ color: colors.neutral90 }}>Loading chart data...</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography sx={{ color: colors.red200, textAlign: 'center' }}>Failed to load chart data</Typography>
            </Box>
          ) : chartSeries.length > 0 ? (
            <Chart options={chartOptions} series={chartSeries} type='bar' height='100%' />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography sx={{ color: colors.neutral90, textAlign: 'center' }}>
                No distribution data available
              </Typography>
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default FundDistributionChart;
